import { useState, useMemo } from 'react'
import { format } from 'date-fns'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import {
  BarChart3,
  Download,
  Filter,
  X,
  CheckCircle,
  XCircle,
} from 'lucide-react'
import { useExecutions } from '@/features/executions/api/useExecutions'
import { useTestPlans } from '@/features/test-plans/api/useTestPlans'
import { useBuilds } from '@/features/test-plans/api/useBuilds'
import {
  ExecutionStatus,
  getExecutionStatusLabel,
  getExecutionStatusColor,
} from '@/types/execution.types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatDate } from '@/lib/utils'
import { toast } from 'sonner'

const TestResultsPage = () => {
  const [selectedTestPlanId, setSelectedTestPlanId] = useState<string>('all')
  const [selectedBuildId, setSelectedBuildId] = useState<string>('all')
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  const [showFilters, setShowFilters] = useState(false)

  const { data: executionsData, isLoading } = useExecutions()
  const { data: testPlans } = useTestPlans()
  const { data: buildsData } = useBuilds()

  // Filter executions
  const filteredExecutions = useMemo(() => {
    let filtered = Array.isArray(executionsData) ? executionsData : []

    if (selectedTestPlanId !== 'all') {
      filtered = filtered.filter((e) => e.testplan_id === parseInt(selectedTestPlanId))
    }
    if (selectedBuildId !== 'all') {
      filtered = filtered.filter((e) => e.build_id === parseInt(selectedBuildId))
    }
    if (startDate) {
      filtered = filtered.filter(
        (e) => e.execution_ts && new Date(e.execution_ts) >= new Date(startDate)
      )
    }
    if (endDate) {
      filtered = filtered.filter(
        (e) => e.execution_ts && new Date(e.execution_ts) <= new Date(endDate)
      )
    }
    return filtered
  }, [executionsData, selectedTestPlanId, selectedBuildId, startDate, endDate])

  // Calculate comprehensive statistics
  const stats = useMemo(() => {
    const total = filteredExecutions.length
    const passed = filteredExecutions.filter((e) => e.status === ExecutionStatus.Passed).length
    const failed = filteredExecutions.filter((e) => e.status === ExecutionStatus.Failed).length
    const blocked = filteredExecutions.filter((e) => e.status === ExecutionStatus.Blocked).length
    const pending = filteredExecutions.filter((e) => e.status === ExecutionStatus.Pending).length
    const running = filteredExecutions.filter((e) => e.status === ExecutionStatus.Running).length
    const executed = passed + failed + blocked
    const passRate = executed > 0 ? (passed / executed) * 100 : 0
    const completionRate = total > 0 ? (executed / total) * 100 : 0

    return { total, passed, failed, blocked, pending, running, executed, passRate, completionRate }
  }, [filteredExecutions])

  // Test plan breakdown
  const testPlanBreakdown = useMemo(() => {
    const plans = Array.isArray(testPlans) ? testPlans : []
    return plans
      .map((plan) => {
        const planExecs = filteredExecutions.filter((e) => e.testplan_id === plan.id)
        const total = planExecs.length
        const passed = planExecs.filter((e) => e.status === ExecutionStatus.Passed).length
        const failed = planExecs.filter((e) => e.status === ExecutionStatus.Failed).length
        const executed = passed + failed
        const passRate = executed > 0 ? ((passed / executed) * 100).toFixed(1) : '0.0'
        return { id: plan.id, name: plan.name, total, passed, failed, passRate }
      })
      .filter((p) => p.total > 0)
      .sort((a, b) => b.total - a.total)
  }, [testPlans, filteredExecutions])

  const activeFiltersCount = [
    selectedTestPlanId !== 'all',
    selectedBuildId !== 'all',
    startDate.length > 0,
    endDate.length > 0,
  ].filter(Boolean).length

  const handleClearFilters = () => {
    setSelectedTestPlanId('all')
    setSelectedBuildId('all')
    setStartDate('')
    setEndDate('')
  }

  const handleExportPDF = () => {
    if (filteredExecutions.length === 0) {
      toast.error('No data to export')
      return
    }

    const doc = new jsPDF()
    const pageHeight = doc.internal.pageSize.height

    // Title
    doc.setFontSize(20)
    doc.text('Test Results Report', 14, 20)

    doc.setFontSize(10)
    doc.setTextColor(100, 100, 100)
    doc.text(`Generated: ${format(new Date(), 'yyyy-MM-dd HH:mm')}`, 14, 28)
    doc.setTextColor(0, 0, 0)

    // Summary
    let yPos = 38
    doc.setFontSize(14)
    doc.text('Summary', 14, yPos)
    yPos += 7

    autoTable(doc, {
      startY: yPos,
      head: [['Metric', 'Value']],
      body: [
        ['Total Executions', stats.total.toString()],
        ['Passed', stats.passed.toString()],
        ['Failed', stats.failed.toString()],
        ['Blocked', stats.blocked.toString()],
        ['Pass Rate', `${stats.passRate.toFixed(1)}%`],
        ['Completion Rate', `${stats.completionRate.toFixed(1)}%`],
      ],
      theme: 'grid',
      headStyles: { fillColor: [25, 118, 210], textColor: [255, 255, 255] },
      margin: { left: 14, right: 14 },
      columnStyles: { 1: { halign: 'right' } },
    })

    yPos = (doc as any).lastAutoTable.finalY + 15

    // Test Plan Breakdown
    if (testPlanBreakdown.length > 0) {
      if (yPos > pageHeight - 60) {
        doc.addPage()
        yPos = 20
      }

      doc.setFontSize(14)
      doc.text('Test Plan Breakdown', 14, yPos)
      yPos += 7

      autoTable(doc, {
        startY: yPos,
        head: [['Test Plan', 'Total', 'Passed', 'Failed', 'Pass Rate']],
        body: testPlanBreakdown.map((plan) => [
          plan.name,
          plan.total.toString(),
          plan.passed.toString(),
          plan.failed.toString(),
          `${plan.passRate}%`,
        ]),
        theme: 'grid',
        headStyles: { fillColor: [25, 118, 210], textColor: [255, 255, 255] },
        margin: { left: 14, right: 14 },
        columnStyles: {
          1: { halign: 'right' },
          2: { halign: 'right' },
          3: { halign: 'right' },
          4: { halign: 'right' },
        },
      })

      yPos = (doc as any).lastAutoTable.finalY + 15
    }

    // Detailed Execution Results
    if (filteredExecutions.length > 0) {
      doc.addPage()
      yPos = 20

      doc.setFontSize(14)
      doc.text('Detailed Execution Results', 14, yPos)
      yPos += 7

      const executionData = filteredExecutions.slice(0, 100).map((exec) => [
        exec.testcase_name || `TC-${exec.tcversion_id}`,
        exec.testplan_name || `TP-${exec.testplan_id}`,
        getExecutionStatusLabel(exec.status),
        exec.execution_ts ? format(new Date(exec.execution_ts), 'yyyy-MM-dd') : 'N/A',
      ])

      autoTable(doc, {
        startY: yPos,
        head: [['Test Case', 'Test Plan', 'Status', 'Executed At']],
        body: executionData,
        theme: 'striped',
        headStyles: { fillColor: [25, 118, 210], textColor: [255, 255, 255] },
        margin: { left: 14, right: 14 },
        styles: { fontSize: 8, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 60 },
          1: { cellWidth: 50 },
          2: { cellWidth: 30 },
          3: { cellWidth: 35 },
        },
      })

      if (filteredExecutions.length > 100) {
        yPos = (doc as any).lastAutoTable.finalY + 10
        doc.setFontSize(8)
        doc.setTextColor(150, 150, 150)
        doc.text(
          `Showing first 100 of ${filteredExecutions.length} total executions.`,
          14,
          yPos
        )
      }
    }

    doc.save(`test-results-report-${format(new Date(), 'yyyyMMdd')}.pdf`)
    toast.success('PDF report exported successfully')
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Test Results</h1>
          <p className="text-muted-foreground mt-1">
            Aggregated test execution results and analysis
          </p>
        </div>
        <Button onClick={handleExportPDF} variant="outline" disabled={filteredExecutions.length === 0}>
          <Download className="mr-2 h-4 w-4" />
          Export PDF
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Passed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <span className="text-2xl font-bold text-green-600">{stats.passed}</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Failed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-red-500" />
              <span className="text-2xl font-bold text-red-600">{stats.failed}</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Pass Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${stats.passRate >= 80 ? 'text-green-600' : stats.passRate >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
              {stats.passRate.toFixed(1)}%
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Completion</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              <div className="text-2xl font-bold">{stats.completionRate.toFixed(1)}%</div>
              <Progress value={stats.completionRate} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Execution Details</CardTitle>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}>
                <Filter className="mr-2 h-4 w-4" />
                Filters
                {activeFiltersCount > 0 && (
                  <Badge variant="secondary" className="ml-2">{activeFiltersCount}</Badge>
                )}
              </Button>
              {activeFiltersCount > 0 && (
                <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                  <X className="mr-1 h-4 w-4" />
                  Clear
                </Button>
              )}
            </div>
          </div>
        </CardHeader>

        {showFilters && (
          <CardContent className="border-b pb-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Test Plan</label>
                <Select value={selectedTestPlanId} onValueChange={setSelectedTestPlanId}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Test Plans" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Test Plans</SelectItem>
                    {(Array.isArray(testPlans) ? testPlans : []).map((plan) => (
                      <SelectItem key={plan.id} value={plan.id.toString()}>
                        {plan.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Build</label>
                <Select value={selectedBuildId} onValueChange={setSelectedBuildId}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Builds" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Builds</SelectItem>
                    {(Array.isArray(buildsData) ? buildsData : []).map((build) => (
                      <SelectItem key={build.id} value={build.id.toString()}>
                        {build.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Start Date</label>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">End Date</label>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>
            </div>
          </CardContent>
        )}

        <CardContent className={showFilters ? 'pt-4' : ''}>
          {/* Test Plan Breakdown */}
          {testPlanBreakdown.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold mb-3">Test Plan Breakdown</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {testPlanBreakdown.slice(0, 6).map((plan) => (
                  <div key={plan.id} className="border rounded-lg p-3">
                    <div className="font-medium text-sm truncate">{plan.name}</div>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-xs text-muted-foreground">
                        {plan.passed}/{plan.total} passed
                      </span>
                      <span
                        className={`text-sm font-semibold ${
                          parseFloat(plan.passRate) >= 80
                            ? 'text-green-600'
                            : parseFloat(plan.passRate) >= 50
                            ? 'text-yellow-600'
                            : 'text-red-600'
                        }`}
                      >
                        {plan.passRate}%
                      </span>
                    </div>
                    <Progress value={parseFloat(plan.passRate)} className="mt-1" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Executions Table */}
          {isLoading ? (
            <div className="text-center py-12 text-muted-foreground">Loading test results...</div>
          ) : filteredExecutions.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Test Case</TableHead>
                    <TableHead>Test Plan</TableHead>
                    <TableHead>Build</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Tester</TableHead>
                    <TableHead>Executed At</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredExecutions.slice(0, 100).map((exec) => (
                    <TableRow key={exec.id}>
                      <TableCell className="font-medium">
                        {exec.testcase_name || `TC-${exec.tcversion_id}`}
                      </TableCell>
                      <TableCell>{exec.testplan_name || `TP-${exec.testplan_id}`}</TableCell>
                      <TableCell>{exec.build_name || `Build-${exec.build_id}`}</TableCell>
                      <TableCell>
                        <Badge className={getExecutionStatusColor(exec.status)}>
                          {getExecutionStatusLabel(exec.status)}
                        </Badge>
                      </TableCell>
                      <TableCell>{exec.tester_name || '-'}</TableCell>
                      <TableCell>{formatDate(exec.execution_ts)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {filteredExecutions.length > 100 && (
                <p className="text-center text-sm text-muted-foreground mt-4">
                  Showing first 100 of {filteredExecutions.length} results. Export PDF for the full report.
                </p>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {activeFiltersCount > 0
                  ? 'No results match your filters'
                  : 'No test executions recorded yet'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default TestResultsPage
